import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Footprints } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Footprints className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-xl font-bold tracking-tight text-foreground">
            Step<span className="text-primary">Zone</span>
          </span>
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="login-card">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">Owner Login</h1>
          <p className="mt-1 font-body text-sm text-muted-foreground">Sign in to review submissions.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="mb-2 block font-body text-sm font-semibold text-foreground">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@step-zone.co" className="h-12 rounded-xl border-2 border-input" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-2 block font-body text-sm font-semibold text-foreground">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 rounded-xl border-2 border-input" />
            </div>
            {error && <p className="font-body text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-xl bg-primary py-6 font-heading font-semibold text-primary-foreground hover:bg-primary/90">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center font-body text-sm text-muted-foreground">
          <a href="/" className="hover:text-primary">← Back to site</a>
        </p>
      </div>
    </div>
  );
}
